import { useCallback, useEffect, useMemo, useState } from 'react'
import { apiJson, ApiError } from '../api'
import type { ThemeTokens } from '../theme'
import { pageStyles } from '../styles'
import { DataTable } from '../components/DataTable'
import type { DataTableColumn } from '../components/DataTable'
import { BurnBar } from '../components/BurnBar'
import { StatCard } from '../components/StatCard'
import { BurnPanel } from '../components/BurnPanel'
import { DEFAULT_USER_BUDGET, DEV_KEY_PREFIX, UNLIMITED_BUDGET } from '../constants'

/**
 * ROUND 13 (Unit 4): narrowed to PLATFORM KEYS plus a summary strip.
 *
 * The per-customer chat-ceiling table (with its drill-down modal and top
 * spenders aside) that used to live on this page is gone -- not deleted,
 * MOVED: it is the exact same data People's own Keys & Budgets tab already
 * shows per person, and having it on two pages meant two places that could
 * (and once did) disagree about which ledger a number came from. This page
 * now answers "what are the PLATFORM keys costing", which is a genuinely
 * different question from "what is any one person costing" -- see the
 * review at docs/design/specs/2026-09-23-admin-console-review.md section 3.
 *
 * THE SUMMARY STRIP'S THREE FIGURES COME FROM THREE DIFFERENT LEDGERS, ON
 * PURPOSE, and each is labelled with which:
 *   chat / developer  `/api/dash/budget`'s month-to-date split, resolved
 *                      through SPEND_IDENTITY_EMAIL/SPEND_SURFACE_IS_CODE --
 *                      the Sydney CALENDAR MONTH, not a customer or key's
 *                      own lifetime/cycle spend (see that route's own doc
 *                      comment for the David-Barnes-shaped trap this avoids).
 *   platform key      `/api/budgets/platform`'s live `/key/list` spend,
 *                      which resets on the KEY'S OWN budget_duration, not
 *                      the calendar month -- same figure this page already
 *                      showed, just summed.
 *
 * CEILING SUMS treat "no explicit cap" differently per ledger, because the
 * gateway does too: a chat customer with no override gets LiteLLM's
 * `max_end_user_budget` DEFAULT applied automatically ($100, real and
 * enforced), so null is summed AT that default. A developer or platform
 * KEY with no `max_budget` is genuinely UNLIMITED at the gateway -- keys
 * carry no equivalent default -- so those are excluded from the sum and
 * counted separately rather than invented a default that does not exist.
 */

/** `/customer/list` entries (LiteLLM "customer" == distinct `end_user`
 *  value). Used here only to compute the chat ceiling sum; the page no
 *  longer renders one row per customer -- see People's Keys & Budgets tab
 *  for that. */
interface GatewayCustomer {
  user_id: string
  spend?: number
  max_budget?: number | null
}

/** `/key/list` entries -- platform keys rendered here; developer keys read
 *  only for the ceiling sum (never rendered: that table is Keys.tsx's job). */
interface GatewayKey {
  token?: string
  key_alias?: string
  spend?: number
  max_budget?: number | null
  [key: string]: unknown
}

/** Matches `GET /api/dash/budget`'s relevant fields. */
interface BudgetSummary {
  chatMonthToDate: number | string
  developerMonthToDate: number | string
  keyOwnersAvailable: boolean
}

function money(value: number | undefined): string {
  return `$${(value ?? 0).toFixed(2)}`
}

type CapKind = 'unlimited' | 'default' | 'explicit'

function capKind(cap: number | null | undefined): CapKind {
  if (cap == null) {
    return 'default'
  }
  if (cap >= UNLIMITED_BUDGET) {
    return 'unlimited'
  }
  return 'explicit'
}

function capLabel(cap: number | null | undefined): string {
  const kind = capKind(cap)
  if (kind === 'default') {
    return `Default ($${DEFAULT_USER_BUDGET})`
  }
  if (kind === 'unlimited') {
    return 'Unlimited'
  }
  return `$${(cap as number).toLocaleString()}`
}

/** A finite ceiling to add to a sum, or null when this row should be
 *  EXCLUDED and counted separately instead -- unlimited, or (for a key,
 *  never a chat customer) no explicit cap at all. */
function financeableCap(cap: number | null | undefined, treatNullAsDefault: boolean): number | null {
  if (cap != null && cap >= UNLIMITED_BUDGET) return null
  if (cap == null) return treatNullAsDefault ? DEFAULT_USER_BUDGET : null
  return cap
}

function sumCeilings(caps: Array<number | null | undefined>, treatNullAsDefault: boolean): { sum: number; excluded: number } {
  let sum = 0
  let excluded = 0
  for (const cap of caps) {
    const finite = financeableCap(cap, treatNullAsDefault)
    if (finite === null) {
      excluded += 1
    } else {
      sum += finite
    }
  }
  return { sum, excluded }
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    return `${err.message} (HTTP ${err.status})`
  }
  return err instanceof Error ? err.message : String(err)
}

export function BudgetsPage({ tokens }: { tokens: ThemeTokens }) {
  const styles = useMemo(() => pageStyles(tokens), [tokens])

  // Read only for the chat ceiling sum below -- never rendered as a table.
  const [customers, setCustomers] = useState<GatewayCustomer[]>([])
  const [customersError, setCustomersError] = useState<string | null>(null)

  // Read only for the developer-key ceiling sum -- Keys.tsx owns the table.
  const [allKeys, setAllKeys] = useState<GatewayKey[]>([])
  const [allKeysError, setAllKeysError] = useState<string | null>(null)

  const [summary, setSummary] = useState<BudgetSummary | null>(null)
  const [summaryError, setSummaryError] = useState<string | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(true)

  const [platformKeys, setPlatformKeys] = useState<GatewayKey[]>([])
  const [keysLoading, setKeysLoading] = useState(true)
  const [keysError, setKeysError] = useState<string | null>(null)
  const [keyCapDrafts, setKeyCapDrafts] = useState<Record<string, string>>({})
  const [keyBusy, setKeyBusy] = useState<string | null>(null)

  // Platform keys: sortable, filterable -- its own clear job, per the
  // review, unclouded by a per-customer table that was never really about
  // platform keys.
  const [platformQuery, setPlatformQuery] = useState('')
  const [platformSortKey, setPlatformSortKey] = useState<'spend' | 'alias'>('spend')
  const [platformSortAsc, setPlatformSortAsc] = useState(false)

  function togglePlatformSort(key: 'spend' | 'alias') {
    if (platformSortKey === key) {
      setPlatformSortAsc((v) => !v)
    } else {
      setPlatformSortKey(key)
      setPlatformSortAsc(key === 'alias')
    }
  }

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true)
    setSummaryError(null)
    try {
      setSummary(await apiJson<BudgetSummary>('/api/dash/budget?days=30'))
    } catch (err) {
      setSummaryError(errorMessage(err))
    } finally {
      setSummaryLoading(false)
    }
  }, [])

  const loadCustomers = useCallback(async () => {
    setCustomersError(null)
    try {
      const data = await apiJson<{ users: GatewayCustomer[] }>('/api/budgets/users')
      setCustomers(data.users)
    } catch (err) {
      setCustomersError(errorMessage(err))
    }
  }, [])

  const loadAllKeys = useCallback(async () => {
    setAllKeysError(null)
    try {
      const data = await apiJson<{ keys: GatewayKey[] }>('/api/keys')
      setAllKeys(data.keys)
    } catch (err) {
      setAllKeysError(errorMessage(err))
    }
  }, [])

  const loadPlatformKeys = useCallback(async () => {
    setKeysLoading(true)
    setKeysError(null)
    try {
      const data = await apiJson<{ keys: GatewayKey[] }>('/api/budgets/platform')
      setPlatformKeys(data.keys)
    } catch (err) {
      setKeysError(errorMessage(err))
    } finally {
      setKeysLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadSummary()
    void loadCustomers()
    void loadAllKeys()
    void loadPlatformKeys()
  }, [loadSummary, loadCustomers, loadAllKeys, loadPlatformKeys])

  async function setPlatformKeyBudget(key: string, maxBudget: number) {
    setKeyBusy(key)
    setKeysError(null)
    try {
      await apiJson('/api/budgets/platform', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, maxBudget }),
      })
      setPlatformKeys((prev) => prev.map((k) => (k.token === key ? { ...k, max_budget: maxBudget } : k)))
      setKeyCapDrafts((prev) => ({ ...prev, [key]: String(maxBudget) }))
    } catch (err) {
      setKeysError(errorMessage(err))
    } finally {
      setKeyBusy(null)
    }
  }

  const chatSpend = Number(summary?.chatMonthToDate ?? 0)
  const developerSpend = Number(summary?.developerMonthToDate ?? 0)
  const platformSpend = platformKeys.reduce((sum, k) => sum + (k.spend ?? 0), 0)

  // Chat: a null override gets the gateway's real DEFAULT applied, so it
  // belongs IN the sum, at that default.
  const chatCeiling = useMemo(() => sumCeilings(customers.map((c) => c.max_budget), true), [customers])
  // Developer keys: no equivalent default exists at the key level, so a
  // null max_budget is genuinely unlimited and excluded, counted instead.
  const developerKeys = useMemo(() => allKeys.filter((k) => (k.key_alias ?? '').toLowerCase().startsWith(DEV_KEY_PREFIX)), [allKeys])
  const developerCeiling = useMemo(() => sumCeilings(developerKeys.map((k) => k.max_budget), false), [developerKeys])
  const platformCeiling = useMemo(() => sumCeilings(platformKeys.map((k) => k.max_budget), false), [platformKeys])

  const shownPlatformKeys = useMemo(() => {
    const needle = platformQuery.trim().toLowerCase()
    const filtered = needle
      ? platformKeys.filter((k) => (k.key_alias ?? k.token ?? '').toLowerCase().includes(needle))
      : platformKeys
    const dir = platformSortAsc ? 1 : -1
    return [...filtered].sort((a, b) => {
      if (platformSortKey === 'alias') {
        return (a.key_alias ?? a.token ?? '').localeCompare(b.key_alias ?? b.token ?? '') * dir
      }
      return ((a.spend ?? 0) - (b.spend ?? 0)) * dir
    })
  }, [platformKeys, platformQuery, platformSortKey, platformSortAsc])

  const platformColumns: Array<DataTableColumn<GatewayKey>> = [
    { key: 'alias', header: 'Key', render: (k) => k.key_alias ?? (k.token ? `${k.token.slice(0, 12)}…` : '?') },
    { key: 'spend', header: 'Spend', render: (k) => money(k.spend), align: 'right' },
    {
      key: 'burn',
      header: 'Burn',
      render: (k) => <BurnBar spend={k.spend ?? 0} cap={k.max_budget ?? null} tokens={tokens} />,
    },
    { key: 'cap', header: 'Ceiling', render: (k) => capLabel(k.max_budget ?? null) },
    {
      key: 'actions',
      header: 'Actions',
      render: (k) => {
        if (!k.token) {
          return <span style={styles.hint}>no key token</span>
        }
        const token = k.token
        const draft = keyCapDrafts[token] ?? ''
        const busy = keyBusy === token
        const parsedDraft = Number(draft)
        const canSubmit = draft.trim() !== '' && !Number.isNaN(parsedDraft) && parsedDraft >= 0
        return (
          <div style={{ ...styles.actionsCell, flexWrap: 'nowrap' }}>
            <input
              aria-label={`New ceiling for ${k.key_alias ?? token}`}
              type="number"
              min={0}
              step="1"
              value={draft}
              onChange={(event) => {
                const { value } = event.target
                setKeyCapDrafts((prev) => ({ ...prev, [token]: value }))
              }}
              style={{ ...styles.input, width: '5.5rem' }}
              disabled={busy}
            />
            <button
              type="button"
              style={styles.buttonGhost}
              disabled={busy || !canSubmit}
              onClick={() => void setPlatformKeyBudget(token, parsedDraft)}
            >
              Set cap
            </button>
          </div>
        )
      },
    },
  ]

  return (
    <div style={{ ...styles.page, maxWidth: 'none' }}>
      {summaryError ? <p style={styles.error}>{summaryError}</p> : null}
      {customersError ? <p style={styles.error}>Chat ceiling sum: {customersError}</p> : null}
      {allKeysError ? <p style={styles.error}>Developer ceiling sum: {allKeysError}</p> : null}
      <div style={styles.statsRow}>
        <StatCard
          label="Chat spend this month"
          value={summaryLoading ? '…' : money(chatSpend)}
          tokens={tokens}
          hint={`of ${money(chatCeiling.sum)} ceiling${chatCeiling.excluded ? ` (${chatCeiling.excluded} unlimited)` : ''}`}
        />
        <StatCard
          label="Developer spend this month"
          value={summaryLoading ? '…' : money(developerSpend)}
          tokens={tokens}
          hint={`of ${money(developerCeiling.sum)} ceiling${developerCeiling.excluded ? ` (${developerCeiling.excluded} no cap)` : ''}`}
        />
        <StatCard
          label="Platform key spend"
          value={keysLoading ? '…' : money(platformSpend)}
          tokens={tokens}
          hint={`of ${money(platformCeiling.sum)} ceiling${platformCeiling.excluded ? ` (${platformCeiling.excluded} no cap)` : ''} · current period, not calendar month`}
        />
      </div>
      {summary && !summary.keyOwnersAvailable ? (
        <p style={styles.hint}>
          The developer-key roster could not be read just now, so developer spend above may be undercounted (those
          rows fall back to chat) until it can.
        </p>
      ) : null}
      <p style={styles.sectionSubtitle}>
        Chat and developer spend are this Sydney calendar month, resolved the same way every other per-person
        figure on this platform is. For any one person's two ceilings side by side, or to change either, see their{' '}
        <a href="/people" style={{ color: tokens.brand }}>
          People page
        </a>
        .
      </p>

      {/* Burn first: the question "who is about to be refused" is the one
          somebody opens this page to answer, and the table below is the
          reference you reach for once you know whose row to find. */}
      <BurnPanel tokens={tokens} />

      <section style={styles.section}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Platform key ceilings</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="search"
              value={platformQuery}
              onChange={(e) => setPlatformQuery(e.target.value)}
              placeholder="Filter by key alias"
              aria-label="Filter platform keys"
              style={{ ...styles.input, width: '14rem' }}
            />
            <span style={styles.hint}>Sort</span>
            <button
              type="button"
              style={platformSortKey === 'spend' ? styles.button : styles.buttonGhost}
              onClick={() => togglePlatformSort('spend')}
            >
              Spend {platformSortKey === 'spend' ? (platformSortAsc ? '↑' : '↓') : ''}
            </button>
            <button
              type="button"
              style={platformSortKey === 'alias' ? styles.button : styles.buttonGhost}
              onClick={() => togglePlatformSort('alias')}
            >
              Key {platformSortKey === 'alias' ? (platformSortAsc ? '↑' : '↓') : ''}
            </button>
            <button
              type="button"
              style={styles.buttonGhost}
              onClick={() => void loadPlatformKeys()}
              disabled={keysLoading}
            >
              {keysLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <p style={styles.sectionSubtitle}>
          Service-key ceilings (LibreChat's shared gateway key and any other platform-level keys). A key's spend
          resets every budget duration, so the platform key's spend is effectively the current period's platform
          burn.
        </p>
        {keysError ? <p style={styles.error}>{keysError}</p> : null}
        {keysLoading ? (
          <p style={styles.hint}>Loading platform keys…</p>
        ) : (
          <DataTable
            columns={platformColumns}
            rows={shownPlatformKeys}
            getRowKey={(k) => k.token ?? k.key_alias ?? 'unknown-key'}
            getRowLabel={(k) => k.key_alias ?? k.token ?? 'key'}
            tokens={tokens}
            emptyLabel="No platform keys found."
          />
        )}
      </section>
    </div>
  )
}

export default BudgetsPage
